/**
 * Permanent redirect map for retired URLs.
 *
 * Each entry maps an OLD path → NEW path. The <LegacyRedirects /> component
 * watches the current route on every render and issues a `<Navigate replace>`
 * with `replace` so the old URL never shows up in browser history, and so
 * search-engine link equity flows to the new URL.
 *
 * To add a redirect:
 *   1. Identify the old URL from GSC's "Not found (404)" report
 *   2. Pick the closest still-existing canonical page
 *   3. Add the pair to REDIRECTS (or REDIRECT_PREFIXES for directory moves)
 *   4. Redeploy. Then in GSC: Pages → "Not found (404)" → Validate fix.
 */

// Exact-match redirects (most precise — wins over prefix matches).
// Pathnames only — the host (`www.` or apex) is handled at the CDN/ingress
// level before requests hit React Router.
export const REDIRECTS = {
  // WordPress-era "services" URL → current umbrella "solutions" page.
  "/services": "/solutions",

  // Old WordPress /products/logo-mats/... slugs → current product pages.
  // These SKUs were renamed during the catalog rebuild in early 2026.
  "/products/logo-mats/spectra-print": "/products/spectra-print-hd",
  "/products/logo-mats/premier-nop-custom-matting": "/products/premier-nop-customs-clm",

  // WordPress "Natures View Vinyl Flooring" family lived under a two-level
  // slug (family / size); the current catalog stores each SKU under its
  // own flat slug.
  "/products/natures-view-vinyl-flooring/18x36-lvt": "/products/nature-view-18x36-lvt",
  "/products/natures-view-vinyl-flooring/18x18": "/products/nature-view-rustique-18x18",
  "/products/natures-view-vinyl-flooring/7x48": "/products/nature-view-rustique-7x48",
  "/products/natures-view-vinyl-flooring/6x36": "/products/nature-view-rustique-6x36",
  "/products/natures-view-vinyl-flooring": "/products?category=luxury-vinyl",

  // Legacy category "landing pages" → current filtered catalog view.
  "/products/non-slip-flooring": "/products?category=non-slip-flooring",
  "/products/commercial-matting": "/products?category=commercial-mats",

  // Retired WordPress-era product PDFs → the current product page where the
  // most-recent versions of the InfoSpec and TechSpec PDFs are linked.
  "/wp-content/downloads/InfoSpecs_NVR18x18_PS.pdf":
    "/products/nature-view-rustique-18x18",
};

// Prefix-match redirects: any URL starting with `oldPrefix` is rewritten so
// the remainder appends to `newPrefix`. Used when an entire section moves.
//
// Example: "/blog/installation-tips" with "/blog/" → "/resources/" becomes
//          "/resources/installation-tips"
export const REDIRECT_PREFIXES = {
  // Example: "/blog/": "/resources/",
};

/**
 * Normalize a pathname for matching: strip trailing slash (except "/"),
 * lowercase. Returns the new path if it should be redirected, or null
 * if the original path stands.
 */
export function resolveRedirect(rawPath) {
  if (!rawPath) return null;
  const path = rawPath === "/" ? "/" : rawPath.replace(/\/+$/, "");

  if (REDIRECTS[path]) return REDIRECTS[path];

  for (const [oldPrefix, newPrefix] of Object.entries(REDIRECT_PREFIXES)) {
    if (path.startsWith(oldPrefix)) {
      return newPrefix + path.slice(oldPrefix.length);
    }
  }
  return null;
}
